<template>
	<div
		class="multilevel-select"
		:class="{
			'multilevel-select--expand': show_dropdown
		}"
	>
		<button
			class="multilevel-select__toggle"
			type="button"
			@click="show_dropdown = !show_dropdown"
		>
			<p class="multilevel-select__toggle-label">
				<span>
					{{ input_label }}
				</span>
			</p>
		</button>
		<div class="multilevel-select__content">
			<template v-if="level_1.length > 0">
				<ul
					class="multilevel-select__top-select"
					:class="{
						'multilevel-select__top-select--hidden': hide_top_select
					}"
				>
					<li
						v-for="location_1 in level_1"
						:key="'location-' + location_1.id"
					>
						<button
							type="button"
							:value="location_1.id"
							@click="setLocationValue( location_1.id, ( location_1.children && location_1.children.length > 0 ) ? true : false )"
						>
							{{ location_1.title }}
						</button>
					</li>
				</ul>
				<template
					v-for="location_1 in level_1"
					:key="'location-' + location_1.id"
				>
					<template v-if="location_1.children.length > 0">
						<ul
							:id="'multilevel-select-child-' + location_1.id"
							class="multilevel-select__child-select"
							:class="{
								'multilevel-select__child-select--visible': visible_child_select === location_1.id
							}"
						>
							<li class="multilevel-select__back-buttons">
								<button
									type="button"
									class="multilevel-select__back-button"
									@click="closeAllLocationLevels()"
								>
									<span class="u">
										{{ location_1.title }}
									</span>
								</button>
							</li>
							<li
								v-for="location_2 in location_1.children"
								:key="'location-' + location_2.id"
							>
								<button
									type="button"
									:value="location_2.id"
									@click="setLocationValue( location_2.id, ( location_2.children && location_2.children.length > 0 ) ? true : false )"
								>
									{{ location_2.title }}
								</button>
							</li>
						</ul>
						<template
							v-for="location_2 in location_1.children"
							:key="'outer-location-' + location_2.id"
						>
							<ul
								:id="'multilevel-select-child-' + location_2.id"
								class="multilevel-select__child-select"
								:class="{
									'multilevel-select__child-select--visible': visible_child_select === location_2.id
								}"
							>
								<li class="multilevel-select__back-buttons">
									<button
										type="button"
										class="multilevel-select__back-button"
										@click="closeAllLocationLevels()"
									>
										<span class="u">
											{{ location_1.title }}
										</span>
									</button>
									<button
										type="button"
										class="multilevel-select__back-button"
										@click="setLocationValue( location_1.id, true )"
									>
										<span class="u">
											{{ location_2.title }}
										</span>
									</button>
								</li>
								<li
									v-for="location_3 in location_2.children"
									:key="'location-' + location_3.id"
								>
									<button
										type="button"
										:value="location_3.id"
										@click="setLocationValue( location_3.id, ( location_3.children && location_3.children.length > 0 ) ? true : false )"
									>
										{{ location_3.title }}
									</button>
								</li>
								<li class="multilevel-select__add-location">
									<NewProjectLocationFormComponent
										field-handle="project_location"
										form-title="Add location"
										:parent-id="location_2.id"
										@refresh:project-locations="( value ) => updateProjectLocations( value )"
									/>
								</li>
							</ul>
							<template
								v-for="location_3 in location_2.children"
								:key="'outer-location-' + location_3.id"
							>
								<ul
									:id="'multilevel-select-child-' + location_3.id"
									class="multilevel-select__child-select"
									:class="{
										'multilevel-select__child-select--visible': visible_child_select === location_3.id
									}"
								>
									<li class="multilevel-select__back-buttons">
										<button
											type="button"
											class="multilevel-select__back-button"
											@click="closeAllLocationLevels()"
										>
											<span class="u">
												{{ location_1.title }}
											</span>
										</button>
										<button
											type="button"
											class="multilevel-select__back-button"
											@click="setLocationValue( location_1.id, true )"
										>
											<span class="u">
												{{ location_2.title }}
											</span>
										</button>
										<button
											type="button"
											class="multilevel-select__back-button"
											@click="setLocationValue( location_2.id, true )"
										>
											<span class="u">
												{{ location_3.title }}
											</span>
										</button>
									</li>
									<li
										v-for="location_4 in location_3.children"
										:key="'location-' + location_4.id"
									>
										<button
											type="button"
											:value="location_4.id"
											@click="setLocationValue( location_4.id, ( location_4.children && location_4.children.length > 0 ) ? true : false )"
										>
											{{ location_4.title }}
										</button>
									</li>
									<li class="multilevel-select__add-location">
										<NewProjectLocationFormComponent
											field-handle="project_location"
											form-title="Add location"
											:parent-id="location_3.id"
											@refresh:project-locations="( value ) => updateProjectLocations( value )"
										/>
									</li>
								</ul>
							</template>
						</template>
					</template>
				</template>
			</template>
		</div>
	</div>
</template>


<script setup>
import { useNewProjectStore } from '../stores/new-project.js';
import { ref, watch, defineEmits, defineProps, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { onClickOutside } from '@vueuse/core';
import NewProjectLocationFormComponent from './new-project/NewProjectLocationFormComponent.vue';

const new_project_store = useNewProjectStore();
const { getProjectLocations } = new_project_store;
const {
	project_locations
} = storeToRefs( new_project_store );


const {
	selected_project_location
} = storeToRefs( new_project_store );

const emit = defineEmits( ['select'] );

const props = defineProps( {
	id: {
		required: true,
		type: String
	},
	label: {
		required: true,
		type: String
	},
	placeholder: {
		required: false,
		type: String,
		default: 'Search'
	},
	query: {
		required: false,
		type: String,
		default: ''
	},
	options: {
		required: true,
		type: Array
	},
	searchDisabled: {
		required: false,
		type: Boolean,
		default: false
	},
	loading: {
		required: false,
		type: Boolean,
		default: false
	}
} );

const target = ref( null );
const show_dropdown = ref( false );
const hide_top_select = ref( false );
const visible_child_select = ref( null );
const input_label = ref( 'Select region' );

const level_1 = computed( () => {
	const level_1_array = [];
	props.options.forEach( filter => {
		if ( filter.level === 1 ) {
			level_1_array.push( filter );
		}
	} );

	return level_1_array;
} );

onClickOutside( target, () => show_dropdown.value = false );

watch( selected_project_location, ( value ) => {
	if ( value ) {
		input_label.value = selected_project_location.value.title;
	}
} );

onMounted ( async() => {
	if ( selected_project_location && selected_project_location.value ) {
		input_label.value = selected_project_location.value.title;
	}
} );

function setLocationValue( id, has_children ) {
	if ( has_children === false ) {
		show_dropdown.value = false;
		emit( 'select', id );
		return;
	}

	hide_top_select.value = true;
	visible_child_select.value = id;
}

function closeAllLocationLevels() {
	hide_top_select.value = false;
	visible_child_select.value = null;
}

async function updateProjectLocations( value ) {
	await getProjectLocations();
	if ( !value ) {
		return;
	}
	let item = {};
	project_locations.value.forEach( location => {
		if ( location.title === String( value ) ) {
			item = location;
			if ( !item || !item.title ) {
				return;
			}
		}
	} );
	if ( !item || !item.id ) {
		return;
	}
	setLocationValue( item.id, false );
}

</script>
